import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { IconButton } from "../buttons/iconButton";
import { formatTime } from "../../utils/timeUtiles";
import classnames from "classnames";
import { ProgressBar } from "../inputs/progressBar";
import { loadSvgsIntoCache } from "../../utils/cacheMachine";
import {
  CanvasDataEvent,
  EventPayload,
  EventTypes,
  VideoData,
} from "../../providers/types";
import { VALID_KEYS_WITH_ZOOM } from "../../constants";
import { isVideoViewEventCheck } from "../../utils/eventUtils";
import { useAppContext } from "../../contexts/appContext";
import {
  ActionType,
  CanvasMode,
  MeetingRole,
  State,
} from "../../contexts/types";
import variables from "../../styles/variables.scss";
import { useFocusCheck } from "../../hooks/useFocusCheck";
import { consoleNonProd } from "../../utils/utilityBeltUtils";
import { VideoReplayTimeAndState } from "../replay/replayTypes";
import { isIOS } from "react-device-detect";
import { HelperPopUp } from "../popUps/helperPopUp";

type VideoViewerProps = {
  sourceUrl: string;
  leader: boolean;
  startTime?: number;
  endTime?: number;
  isPreview?: boolean;
  sendEvent?:
    | ((eventPayload: EventPayload, appState: State) => void)
    | undefined;
  events?: CanvasDataEvent[];
  mode?: CanvasMode;
  replayState?: VideoReplayTimeAndState;
};

const VideoViewer: React.FC<VideoViewerProps> = ({
  sourceUrl,
  leader,
  startTime,
  endTime,
  isPreview = false,
  sendEvent,
  events = [],
  mode,
  replayState,
}: VideoViewerProps) => {
  const { meetingRole, state, dispatch } = useAppContext();
  const { playerHasFocus } = useFocusCheck();

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [ready, setReady] = useState(false);
  const [replayVideoLock, setReplayVideoLock] = useState(true);

  const playerRef = useRef<HTMLVideoElement | null>(null);

  const duration = document.getElementById("duration");
  const disableButton = !leader && !state.exploreMode;

  const load = (src?: string) => {
    setVideoState((prevState) => ({
      ...prevState,
      src,
      played: 0,
      loaded: 0,
      pip: false,
    }));
    durationSetup();
  };

  const initialState = {
    src: sourceUrl,
    pip: false,
    playing: false,
    controls: false,
    light: false,
    volume: 0.5,
    muted: isIOS ? true : false,
    played: 0,
    loaded: 0,
    duration: 0,
    playbackRate: 1.0,
    loop: false,
    seeking: false,
    loadedSeconds: 0,
    playedSeconds: 0,
  };

  type PlayerState = Omit<typeof initialState, "src"> & {
    src?: string;
  };

  const [videoState, setVideoState] = useState<PlayerState>(initialState);
  const [lastVideoData, setLastVideoData] = useState<VideoData>(undefined);

  // called once on load to present the user with time for the duration progress bar
  const durationSetup = () => {
    const videoDuration = Math.round(playerRef?.current?.duration);
    setVideoState((prevState) => ({ ...prevState, duration: videoDuration }));
    const time = formatTime(videoDuration);
    if (duration) {
      duration.innerText = `${time?.minutes}:${time?.seconds}`;
      duration.setAttribute("datetime", `${time?.minutes}m ${time?.seconds}s`);
    }
  };
  const handlePlayPause = () => {
    setVideoState((prevState) => ({
      ...prevState,
      playing: !prevState.playing,
    }));
  };

  const handleRateChange = () => {
    const player = playerRef.current;
    if (!player) return;

    setVideoState((prevState) => ({
      ...prevState,
      playbackRate: player.playbackRate,
    }));
  };

  const handlePlay = () => {
    setVideoState((prevState) => ({
      ...prevState,
      playing: true,
    }));
  };

  const handlePause = () => {
    setVideoState((prevState) => ({ ...prevState, playing: false }));
  };

  const handleProgress = () => {
    const player = playerRef.current;
    // We only want to update time slider if we are not currently seeking
    if (!player || videoState.seeking || !player.buffered?.length) return;

    setVideoState((prevState) => ({
      ...prevState,
      loadedSeconds: player.buffered?.end(player.buffered?.length - 1),
      loaded:
        player.buffered?.end(player.buffered?.length - 1) / player.duration,
    }));
  };

  const handleTimeUpdate = () => {
    const player = playerRef.current;
    // We only want to update time slider if we are not currently seeking
    if (!player || videoState.seeking) return;

    if (!player.duration) return;

    setVideoState((prevState) => ({
      ...prevState,
      playedSeconds: player.currentTime,
      played: player.currentTime / player.duration,
    }));
  };

  const handleEnded = () => {
    setVideoState((prevState) => ({ ...prevState, playing: prevState.loop }));
  };

  const handleDurationChange = () => {
    const player = playerRef.current;
    if (!player) return;

    setVideoState((prevState) => ({ ...prevState, duration: player.duration }));
  };

  useEffect(() => {
    load(sourceUrl);
  }, [sourceUrl]);

  const setPlayerRef = useCallback((player: HTMLVideoElement) => {
    const ref = playerRef;
    if (!player) return;
    ref.current = player;
  }, []);

  const handleRewind = () => {
    setVideoState((prevState) => ({ ...prevState, seeking: false }));
    if (playerRef.current) {
      playerRef.current.currentTime = playerRef.current.currentTime - 15;
    }
  };

  const handleFastForward = () => {
    setVideoState((prevState) => ({ ...prevState, seeking: false }));
    if (playerRef.current) {
      playerRef.current.currentTime = playerRef.current.currentTime + 15;
    }
  };

  const handleTimeClick = (e: number) => {
    setVideoState((prevState) => ({ ...prevState, seeking: false }));
    if (playerRef.current) {
      playerRef.current.currentTime = e;
    }
  };

  const sendVideoEvent = useCallback(
    (videoEvent: VideoData, leader: boolean, exploreMode: boolean) => {
      if (sendEvent && leader && !exploreMode) {
        sendEvent(
          {
            type: EventTypes.VIDEO,
            leader: leader,
            videoData: videoEvent,
          },
          state
        );
      }
    },
    [sendEvent, state]
  );

  // on first load of a video board set to focus so
  // everyone starts when instuctor plays the video
  useEffect(() => {
    if (state.exploreMode === true) {
      dispatch({
        type: ActionType.UPDATE_EXPLORE_MODE,
        payload: { exploreMode: false },
      });
      sendEvent &&
        meetingRole === MeetingRole.Presenter &&
        sendEvent(
          {
            type: EventTypes.TOGGLE_EXPLORE_MODE,
            leader: true,
            exploreMode: false,
          },
          state
        );
    }
  }, []);

  // when an instructor starts a video emit out the time and a play event
  // as long as the video is playing to keep any late arrivers or screen refreshers in sync
  // will self clear when the video stops or you navigate away from the video board
  useEffect(() => {
    let playInterval;
    if (videoState.playing && leader) {
      playInterval = setInterval(() => {
        sendVideoEvent(
          {
            videoEvent: "play-pause",
            keepPlaying: true,
            time: playerRef.current?.currentTime,
          },
          leader,
          state.exploreMode
        );
      }, 10000);
    }
    return function () {
      clearInterval(playInterval);
    };
  }, [videoState.playing, leader, state.exploreMode, sendVideoEvent]);

  //preload play / pause / max / min svgs
  useEffect(() => {
    loadSvgsIntoCache([
      "/icons/pause.svg",
      "/icons/playVideo.svg",
      "/icons/minimize-full.svg",
      "/icons/maximize.svg",
    ]);
  }, []);

  useEffect(() => {
    const keyCommander = (inputKey: string) => {
      if (!isPreview && !playerHasFocus()) {
        if (VALID_KEYS_WITH_ZOOM.indexOf(inputKey) < 0) return;
        // w or up arrow
        if (inputKey == "w" || inputKey == "ArrowUp") {
          setVideoState((prevState) => ({
            ...prevState,
            volume: videoState.volume < 1 ? videoState.volume + 0.05 : 1,
          }));
        }
        // s or down arrow
        else if (inputKey == "s" || inputKey == "ArrowDown") {
          setVideoState((prevState) => ({
            ...prevState,
            volume: videoState.volume < 1 ? videoState.volume - 0.05 : 1,
          }));
        }
        // a or left arrow
        else if (inputKey == "a" || inputKey == "ArrowLeft") {
          if (playerRef) {
            handleRewind();
            sendVideoEvent({ videoEvent: "rewind" }, leader, state.exploreMode);
          }
        }
        // d or right arrow
        else if (inputKey == "d" || inputKey == "ArrowRight") {
          if (playerRef) {
            handleFastForward();
            sendVideoEvent(
              { videoEvent: "fast-forward" },
              leader,
              state.exploreMode
            );
          }
        }
        // z key or -
        else if (inputKey == "z" || inputKey == "-") {
          setIsFullScreen(!isFullScreen);
          sendVideoEvent(
            { videoEvent: "full-screen" },
            leader,
            state.exploreMode
          );
        }
        // x key or +
        else if (inputKey == "x" || inputKey == "=" || inputKey == "+") {
          if (videoState.muted) {
            setVideoState((prevState) => ({
              ...prevState,
              volume: 0.5,
              muted: false,
            }));
          } else {
            setVideoState((prevState) => ({
              ...prevState,
              volume: 0,
              muted: true,
            }));
          }
        }
        // space bar to reset
        else if (inputKey == " ") {
          const focusedElement = document?.activeElement;
          if (playerRef && focusedElement?.tagName !== "BUTTON") {
            handlePlayPause();
            sendVideoEvent(
              {
                videoEvent: "play-pause",
                time: playerRef?.current?.currentTime,
              },
              leader,
              state.exploreMode
            );
          }
        }
      }
    };
    function onDocumentKeyDown(event) {
      //event.preventDefault();
      if (leader || state.exploreMode) {
        keyCommander(event?.key);
      }
    }
    document.addEventListener("keydown", onDocumentKeyDown, false);

    return function () {
      document.removeEventListener("keydown", onDocumentKeyDown, false);
    };
  }, [
    videoState.volume,
    state.exploreMode,
    leader,
    isFullScreen,
    videoState.playing,
    videoState.muted,
    isPreview,
    playerHasFocus,
  ]);

  // events logic for live classes
  useEffect(() => {
    if (mode === CanvasMode.REPLAY) {
      return;
    }
    if (events?.length) {
      const canvasDataEv = events[events.length - 1];
      const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);
      if (!canvasDataEv.isSelf && isVideoViewEventCheck(eventPayload)) {
        if (eventPayload) {
          if (playerRef?.current) {
            // Get the current time of the video player in seconds
            const currentTime = playerRef.current.currentTime;

            // Get the target time from the event payload
            const targetTime = eventPayload.videoData.time;

            // Calculate the absolute difference between the times
            const timeDifference = Math.abs(targetTime - currentTime);
            if (eventPayload.type === EventTypes.VIDEO) {
              switch (eventPayload.videoData.videoEvent) {
                case "play-pause":
                  // play from the reoccuring play event emitter on refresh
                  if (
                    eventPayload.videoData.keepPlaying &&
                    !videoState.playing
                  ) {
                    if (playerRef?.current) {
                      // Check if the absolute difference is greater than 10 seconds
                      if (timeDifference > 10) {
                        handleTimeClick(targetTime);
                        handlePlay();
                      }
                    }
                  } else if (
                    eventPayload.videoData.keepPlaying &&
                    videoState.playing
                  ) {
                    // Check if the absolute difference is greater than 10 seconds
                    if (timeDifference > 10) {
                      handleTimeClick(targetTime);
                    }

                    break;
                  } else {
                    // Check if the absolute difference is greater than 10 seconds
                    if (timeDifference > 10) {
                      handleTimeClick(targetTime);
                    }
                    try {
                      handlePlayPause();
                    } catch (error) {
                      consoleNonProd(error);
                    }
                  }
                  break;
                case "rewind":
                  handleRewind();
                  break;
                case "fast-forward":
                  handleFastForward();
                  break;
                case "seek":
                  handleTimeClick(eventPayload.videoData.time);
                  break;
                case "full-screen":
                  setIsFullScreen(!isFullScreen);
                  break;
              }
            }
          }
        }
      }
    }
  }, [events]);

  useEffect(() => {
    // onload find the last video event for reference
    if (mode !== CanvasMode.REPLAY) {
      return;
    }
    const findLastVideoDataEvent = (events) => {
      for (let i = events?.length - 1; i >= 0; i--) {
        const canvasDataEv = events[i];
        const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);
        if (eventPayload?.videoData) {
          return eventPayload?.videoData;
        }
      }
      return null; // No videoData event found
    };
    setLastVideoData(findLastVideoDataEvent(events));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoState.playing]);

  useEffect(() => {
    // events logic for replay
    try {
      if (mode !== CanvasMode.REPLAY) {
        return;
      }
      const player = playerRef?.current;
      const canvasDataEv = events[events.length - 1];
      const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);
      if (eventPayload?.videoData === undefined) {
        setReplayVideoLock(true);
        setLastVideoData(eventPayload.videoData);
      }
      // skipping backwards in time via chapter skip makes sure we don't play
      // when we should be pausing
      if (
        eventPayload.type === EventTypes.CHANGE_SLIDE ||
        eventPayload.type === EventTypes.TOGGLE_EXPLORE_MODE
      ) {
        if (player && ready) {
          handlePause();
          setReplayVideoLock(true);
        }
      }
      if (eventPayload?.videoData && replayState?.isPlaying) {
        setReplayVideoLock(false);
      }
      if (!canvasDataEv.isSelf && isVideoViewEventCheck(eventPayload)) {
        // Get the current time of the video player in seconds
        const currentTime = playerRef.current.currentTime;

        // Get the target time from the event payload
        const targetTime = eventPayload.videoData.time;

        // Calculate the absolute difference between the times
        const timeDifference = Math.abs(targetTime - currentTime);
        if (eventPayload) {
          if (playerRef?.current && !replayVideoLock) {
            if (eventPayload.type === EventTypes.VIDEO) {
              switch (eventPayload.videoData.videoEvent) {
                case "play-pause":
                  // play from the reoccuring play event emitter on refresh
                  if (timeDifference > 10) {
                    handleTimeClick(targetTime);
                  }

                  if (playerRef?.current) {
                    // Check if the absolute difference is greater than 10 seconds
                    if (eventPayload.videoData.time === 0) {
                      handlePlay();
                    } else if (
                      timeDifference > 10 &&
                      !videoState.playing &&
                      eventPayload.videoData.keepPlaying
                    ) {
                      handleTimeClick(targetTime);
                      handlePlay();
                    } else if (
                      !eventPayload.videoData.keepPlaying &&
                      videoState.playing
                    ) {
                      handlePause();
                    } else if (
                      !eventPayload.videoData.keepPlaying &&
                      !videoState.playing
                    ) {
                      handlePlay();
                    } else {
                      handlePlay();
                    }
                  }

                  break;
                case "rewind":
                  handleRewind();
                  break;
                case "fast-forward":
                  handleFastForward();
                  break;
                case "seek":
                  handleTimeClick(eventPayload.videoData.time);
                  break;
                case "full-screen":
                  setIsFullScreen(!isFullScreen);
                  break;
              }
            }
          }
        }
      }
    } catch (error) {}
  }, [events, replayVideoLock]);

  useEffect(() => {
    if (replayState?.isPlaying) {
      setReplayVideoLock(false);
    }
  }, [replayState]);

  // logic for play pause from the main replay timeline controls
  useEffect(() => {
    if (replayState?.isPlaying === false) {
      handlePause();
      setReplayVideoLock(true);

      return;
    }
    if (!lastVideoData) {
      handlePause();
      setReplayVideoLock(true);

      return;
    }
    try {
      const player = playerRef?.current;
      setReplayVideoLock(false);

      if (
        replayState.time >= 0 &&
        mode === CanvasMode.REPLAY &&
        ready &&
        player &&
        !replayVideoLock
      ) {
        switch (replayState.action) {
          case "play":
            handlePlay();
            break;
          case "pause":
            handlePause();
            break;
          case "rwd":
            handleRewind();
            break;
          case "ff":
            handleFastForward();
            break;
          case "seek":
            if (lastVideoData?.time) {
              handleTimeClick(lastVideoData?.time);
            }
            break;
          case "volume":
            setVideoState((prevState) => ({
              ...prevState,
              volume: replayState.volume,
              muted: replayState.volume > 0 ? false : true,
            }));

            break;
        }
      }
    } catch (error) {}
  }, [replayState]);

  return (
    <div
      id="video-viewer-screen"
      className={classnames(`video-screen`, {
        "--full": isFullScreen,
        "--preview": isPreview,
      })}
    >
      <div
        className={classnames(`video-player-container`, {
          "--student": true,
        })}
      >
        {sourceUrl && (
          <ReactPlayer
            autoPlay={false}
            src={sourceUrl}
            height="100%"
            width="100%"
            ref={setPlayerRef}
            controls={false}
            pip={false}
            playing={videoState.playing}
            playbackRate={videoState.playbackRate}
            muted={videoState.muted}
            volume={videoState.volume}
            onReady={() => {
              durationSetup();
              setReady(true);
            }}
            onEnded={handleEnded}
            onProgress={handleProgress}
            onDurationChange={handleDurationChange}
            onPlay={handlePlay}
            onPause={handlePause}
            onRateChange={handleRateChange}
            onTimeUpdate={handleTimeUpdate}
            config={{
              youtube: {
                cc_load_policy: 1,
                cc_lang_pref: "en",
              },
            }}
          />
        )}
      </div>
      {!isPreview && sourceUrl && (
        <div className="controls-container">
          {mode !== CanvasMode.REPLAY && (
            <div className="video-controls-container">
              <IconButton
                onClick={(e) => {
                  if (playerRef) {
                    handlePlayPause();
                    sendVideoEvent(
                      {
                        videoEvent: "play-pause",
                        time: playerRef.current?.currentTime,
                      },
                      leader,
                      state.exploreMode
                    );
                  }
                }}
                iconName={videoState.playing === true ? "pause" : "playVideo"}
                desc={videoState.playing === true ? "pause" : "play"}
                disabled={disableButton}
                stroke={variables.white}
                btnId="play-pause-video-button"
              />
              <IconButton
                onClick={(e) => {
                  if (playerRef) {
                    handleRewind();
                    sendVideoEvent(
                      { videoEvent: "rewind" },
                      leader,
                      state.exploreMode
                    );
                  }
                }}
                iconName="rewind"
                desc="Rewind"
                disabled={disableButton}
                stroke={variables.white}
                btnId="rewind-video-button"
              />
              <IconButton
                onClick={(e) => {
                  if (playerRef) {
                    handleFastForward();
                    sendVideoEvent(
                      { videoEvent: "fast-forward" },
                      leader,
                      state.exploreMode
                    );
                  }
                }}
                iconName="fast-forward"
                desc="Fast forward"
                disabled={disableButton}
                stroke={variables.white}
                btnId="fast-forward-video-button"
              />

              <span className="video-controls-container__video-progress">
                {videoState.playedSeconds && (
                  <time
                    id="time-elapsed"
                    className="video-controls-container--time"
                  >
                    {formatTime(videoState.playedSeconds)?.minutes +
                      ":" +
                      formatTime(videoState.playedSeconds)?.seconds}
                  </time>
                )}
                <div className="video-controls-container__progress-container">
                  <ProgressBar
                    id="progress-bar"
                    value={videoState.playedSeconds}
                    max={videoState?.duration}
                    onClick={(e) => {
                      handleTimeClick(e);
                      sendVideoEvent(
                        { videoEvent: "seek", time: e },
                        leader,
                        state.exploreMode
                      );
                    }}
                    disabled={disableButton}
                  />
                </div>
                {videoState.duration && !Number.isNaN(videoState.duration) && (
                  <time
                    id="duration"
                    className="video-controls-container--time"
                  >
                    {formatTime(videoState.duration).minutes +
                      ":" +
                      formatTime(videoState.duration).seconds}
                  </time>
                )}
              </span>
            </div>
          )}
          <div className="volume-controls-container">
            <IconButton
              onClick={(e) => {
                if (videoState.muted) {
                  setVideoState((prevState) => ({
                    ...prevState,
                    volume: 0.5,
                    muted: false,
                  }));
                } else {
                  setVideoState((prevState) => ({
                    ...prevState,
                    volume: 0,
                    muted: true,
                  }));
                }
              }}
              iconName="volume-x"
              desc="Mute"
              stroke={variables.white}
              btnId="mute-video-volume-button"
            />
            <IconButton
              onClick={(e) => {
                setVideoState((prevState) => ({
                  ...prevState,
                  volume: videoState.volume > 0 ? videoState.volume - 0.05 : 0,
                  muted: false,
                }));
              }}
              iconName="volume-1"
              desc="Volume down"
              stroke={variables.white}
              btnId="lower-volume-video-button"
            />
            <span className="volume-controls-container__volume-progress">
              <div style={{ display: "inline-block", width: "100%" }}>
                <ProgressBar
                  id="volume-bar"
                  value={videoState.volume}
                  max={1}
                  onClick={(e) => {
                    setVideoState((prevState) => ({
                      ...prevState,
                      volume: e,
                    }));
                  }}
                />
              </div>
            </span>
            <IconButton
              onClick={(e) => {
                setVideoState((prevState) => ({
                  ...prevState,
                  volume: videoState.volume < 1 ? videoState.volume + 0.05 : 1,
                  muted: false,
                }));
              }}
              iconName="volume-2"
              desc="Volume up"
              stroke={variables.white}
              btnId="raise-volume-video-button"
            />
            <IconButton
              onClick={(e) => {
                setIsFullScreen(!isFullScreen);
                sendVideoEvent(
                  { videoEvent: "full-screen" },
                  leader,
                  state.exploreMode
                );
              }}
              iconName={isFullScreen ? "minimize-full" : "maximize"}
              desc="Full screen mode"
              disabled={disableButton}
              stroke={variables.white}
              btnId="set-full-screen-video-button"
            />
          </div>
        </div>
      )}
      {isIOS && (
        <HelperPopUp
          bottom={"5px"}
          isTimed={true}
          right={"10"}
          minWidth="200px"
          userMessage="Tap unmute to enable video sound"
        />
      )}
    </div>
  );
};

export default VideoViewer;
